import { pascalCase } from 'es-toolkit';
import { Fragment } from 'react';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';

import { LoadPayRelatedAccount, BusinessRepresentative } from '../../Applicant.types';
import { companyAccountStatusMap, getDeltaFundingMethodLinkStatusDisplay } from '../../Applicant.utils';
import { DDDGroupHeading, DDDRowGroup } from '../../Applicant.StyledComponents';
import { ApplicantKeyValuePair, ApplicantKeyValueTable } from '../ApplicantKeyValuePair';
import { ApplicantItemWrapper } from './ApplicantRelatedAccounts.StyledComponents';
import { RelatedAccountsCard } from './RelatedAccountsCard';

interface SectionProps extends React.ComponentPropsWithRef<'div'> {
    accountsType: string;
    accounts: LoadPayRelatedAccount[];
    isLoading?: boolean;
    isLoadingError?: boolean;
    onRetry?: () => void;
}

export const ApplicantRelatedAccountsSection = ({
    accountsType,
    accounts,
    isLoading = false,
    isLoadingError = false,
    onRetry,
    ...props
}: SectionProps) => {
    const { applicantId } = useApplicantContext();
    const relatedAccountKey = `${accountsType}-related-account`;

    const renderAccount = (account: LoadPayRelatedAccount, i: number) => {
        const relatedAccountKeyIndex = `${relatedAccountKey}-${i}`;
        const isDirectRelationship = account.isDirectlyLinked;

        const skippedKeys = new Set([
            'businessName',
            'isDirectlyLinked',
            'automaticDeltaFundingMethodLinkState',
            'deltaClientId',
        ]);
        const remainingEntries = Object.entries(account).filter(([key]) => !skippedKeys.has(key));
        const accountFieldEntries = remainingEntries.filter(([, v]) => !Array.isArray(v));
        const businessRepEntries = remainingEntries.filter(([, v]) => Array.isArray(v)) as [
            string,
            BusinessRepresentative[],
        ][];

        return (
            <ApplicantItemWrapper key={relatedAccountKeyIndex} data-testid={relatedAccountKeyIndex}>
                <ApplicantKeyValueTable>
                    <ApplicantKeyValuePair
                        dataTestId={`${relatedAccountKeyIndex}-account-type`}
                        label="Related Account Type"
                        value={
                            isDirectRelationship
                                ? 'Created from this Applicant'
                                : 'Suspected Relationship — Could be same Carrier?'
                        }
                    />
                    {account.automaticDeltaFundingMethodLinkState ? (
                        <ApplicantKeyValuePair
                            dataTestId={`${relatedAccountKeyIndex}-automaticDeltaFundingMethodLinkStatus`}
                            label="Automatic Delta Funding Method Link Status"
                            value={getDeltaFundingMethodLinkStatusDisplay(
                                account.automaticDeltaFundingMethodLinkState,
                                account.deltaClientId,
                            )}
                        />
                    ) : null}
                    <ApplicantKeyValuePair
                        dataTestId={`${relatedAccountKeyIndex}-business-name`}
                        label="Business Name"
                        value={
                            !isDirectRelationship && account.externalApplicantId ? (
                                <a
                                    href={`/applicant/${account.externalApplicantId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {account.businessName}{' '}
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            transform: 'rotate(90deg)',
                                        }}
                                    >
                                        {'\u{238B}'}
                                    </span>
                                </a>
                            ) : (
                                account.businessName
                            )
                        }
                    />
                    {accountFieldEntries.map(([accKey, accValue]) => {
                        const relatedAccountKeyIndexKey = `${relatedAccountKeyIndex}-${accKey}`;
                        const displayValue =
                            accKey === 'companyAccountStatus'
                                ? accValue
                                    ? companyAccountStatusMap[accValue as keyof typeof companyAccountStatusMap]
                                    : null
                                : (accValue as React.ReactNode | string | number | boolean);
                        return (
                            <ApplicantKeyValuePair
                                key={relatedAccountKeyIndexKey}
                                dataTestId={relatedAccountKeyIndexKey}
                                label={pascalCase(accKey)}
                                value={displayValue}
                            />
                        );
                    })}
                    {businessRepEntries.flatMap(([accKey, reps]) =>
                        reps.map((item, j) => {
                            const relatedAccountKeyIndexKeyIndex = `${relatedAccountKeyIndex}-${accKey}-${j}`;
                            return (
                                <Fragment key={relatedAccountKeyIndexKeyIndex}>
                                    <DDDGroupHeading>Business Representative</DDDGroupHeading>
                                    <DDDRowGroup data-testid={relatedAccountKeyIndexKeyIndex}>
                                        {Object.entries(item).map(([busiKey, busiValue]) => (
                                            <ApplicantKeyValuePair
                                                key={`${relatedAccountKeyIndexKeyIndex}-${busiKey}`}
                                                dataTestId={`${relatedAccountKeyIndexKeyIndex}-${busiKey}`}
                                                label={pascalCase(busiKey)}
                                                value={busiValue}
                                            />
                                        ))}
                                    </DDDRowGroup>
                                </Fragment>
                            );
                        }),
                    )}
                </ApplicantKeyValueTable>
            </ApplicantItemWrapper>
        );
    };

    return (
        <RelatedAccountsCard
            {...props}
            data-testid={relatedAccountKey}
            title={`${accountsType} Accounts`}
            count={accounts.length}
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={onRetry}
            emptyMessage={`No related ${accountsType} accounts found.`}
            errorMessage={`Could not load ${accountsType} accounts.`}
        >
            {/* Show the suspected accounts first, then show the directly linked one last (if it exists) */}
            {accounts
                .filter((acc) => acc.externalApplicantId !== applicantId)
                // sort by created date descending (newest to oldest)
                .sort(
                    (a, b) =>
                        new Date(b.companyApplicationCreatedAt ?? 0).getTime() -
                        new Date(a.companyApplicationCreatedAt ?? 0).getTime(),
                )
                .map(renderAccount)}
            {accounts
                .filter((acc) => acc.externalApplicantId === applicantId)
                .map(renderAccount)}
        </RelatedAccountsCard>
    );
};
